import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Button, 
  TextField,
  Alert,
  Paper,
  Divider,
  Tooltip,
  IconButton,
  Chip,
  Grid
} from '@mui/material';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SaveIcon from '@mui/icons-material/Save';
import StarIcon from '@mui/icons-material/Star';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import SchoolIcon from '@mui/icons-material/School';
import DiamondIcon from '@mui/icons-material/Diamond';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useLanguage } from '../contexts/LanguageContext';
import { Timestamp } from 'firebase/firestore';

// Define the subscription tiers with their respective access levels
export const SUBSCRIPTION_TIERS = {
  NONE: 'geen',
  ESSENTIAL: 'essential',
  GOLD: 'gold',
  ELITE: 'elite',
  PLATINUM: 'platinum'
};

// Function to check if a user has access to a specific tier
export const hasAccessToTier = (userTier: string, requiredTier: string): boolean => {
  const tierOrder = [
    SUBSCRIPTION_TIERS.NONE, 
    SUBSCRIPTION_TIERS.ESSENTIAL, 
    SUBSCRIPTION_TIERS.GOLD, 
    SUBSCRIPTION_TIERS.ELITE, 
    SUBSCRIPTION_TIERS.PLATINUM
  ];
  
  const userTierIndex = tierOrder.indexOf(userTier.toLowerCase());
  const requiredTierIndex = tierOrder.indexOf(requiredTier.toLowerCase());
  
  // User has access if their tier is equal to or higher than the required tier
  return userTierIndex >= requiredTierIndex;
};

// Define tier configuration for UI
export const TIER_CONFIG = {
  [SUBSCRIPTION_TIERS.NONE]: {
    color: 'default',
    icon: InfoOutlinedIcon,
    bgColor: 'transparent'
  },
  [SUBSCRIPTION_TIERS.ESSENTIAL]: {
    color: 'success',
    icon: SchoolIcon,
    bgColor: 'rgba(76, 175, 80, 0.1)'
  },
  [SUBSCRIPTION_TIERS.GOLD]: {
    color: 'warning',
    icon: StarIcon,
    bgColor: 'rgba(255, 193, 7, 0.1)'
  },
  [SUBSCRIPTION_TIERS.ELITE]: {
    color: 'primary',
    icon: WorkspacePremiumIcon,
    bgColor: 'rgba(63, 81, 181, 0.1)'
  },
  [SUBSCRIPTION_TIERS.PLATINUM]: {
    color: 'secondary',
    icon: DiamondIcon,
    bgColor: 'rgba(156, 39, 176, 0.1)'
  }
};

// Helper function to get Dutch name for tier
const getTierNameInDutch = (tier: string): string => {
  switch(tier.toLowerCase()) {
    case SUBSCRIPTION_TIERS.NONE: return 'Geen';
    case SUBSCRIPTION_TIERS.ESSENTIAL: return 'Essential';
    case SUBSCRIPTION_TIERS.GOLD: return 'Gold';
    case SUBSCRIPTION_TIERS.ELITE: return 'Elite';
    case SUBSCRIPTION_TIERS.PLATINUM: return 'Platinum';
    default: return tier;
  }
};

// Helper function to calculate expiry date
const calculateExpiryDate = (months: number): string => {
  const date = new Date();
  date.setMonth(date.getMonth() + months);
  return date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
};

interface UserSubscriptionManagerProps {
  userId: string;
  currentTier?: string;
  onUpdateSuccess?: () => void;
}

const UserSubscriptionManager: React.FC<UserSubscriptionManagerProps> = ({ 
  userId, 
  currentTier = SUBSCRIPTION_TIERS.NONE,
  onUpdateSuccess
}) => {
  const [subscriptionTier, setSubscriptionTier] = useState(currentTier);
  const [months, setMonths] = useState(9); // Default to 9 months
  const [expiryDate, setExpiryDate] = useState(calculateExpiryDate(9));
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: 'info' });
  const { language } = useLanguage();

  useEffect(() => {
    // Update expiry date when months change
    setExpiryDate(calculateExpiryDate(months));
  }, [months]);

  useEffect(() => {
    // Check if user data exists in Firestore and get current subscription details
    const fetchUserSubscription = async () => {
      try {
        const userDocRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          
          if (userData.subscriptionTier) {
            setSubscriptionTier(userData.subscriptionTier);
          }
          
          if (userData.subscriptionExpiry) {
            // Calculate remaining months if expiry date exists
            const expiryDate = new Date(userData.subscriptionExpiry);
            const today = new Date();
            const diffTime = expiryDate.getTime() - today.getTime();
            const diffMonths = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 30));
            
            if (diffMonths > 0) {
              setMonths(diffMonths);
              setExpiryDate(userData.subscriptionExpiry.split('T')[0]);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching user subscription:", error);
        setMessage({ text: language === 'nl' ? 'Fout bij het ophalen van abonnementsgegevens' : 'Error fetching subscription details', type: 'error' });
      }
    };

    if (userId) {
      fetchUserSubscription();
    }
  }, [userId, language]);

  const handleSaveSubscription = async () => {
    setLoading(true);
    try {
      // Get expiry date as Date object
      const expiryDateObj = new Date(expiryDate);
      
      // Update the 'users' collection (new format with subscriptionTier)
      const userDocRef = doc(db, 'users', userId);
      await updateDoc(userDocRef, {
        subscriptionTier: subscriptionTier,
        subscriptionExpiry: expiryDateObj.toISOString(),
        lastUpdated: new Date().toISOString()
      });
      
      // Also update the 'SAW_users' collection (old format with subscription object)
      const sawUserDocRef = doc(db, 'SAW_users', userId);
      await updateDoc(sawUserDocRef, {
        // For old subscription format
        subscription: {
          tier: subscriptionTier,
          status: 'active',
          premium_end_date: Timestamp.fromDate(expiryDateObj)
        },
        // Also for new subscription format
        subscriptionTier: subscriptionTier,
        subscriptionExpiry: expiryDateObj.toISOString()
      });
      
      setMessage({ 
        text: language === 'nl' ? 'Abonnement succesvol bijgewerkt' : 'Subscription updated successfully', 
        type: 'success' 
      });
      
      if (onUpdateSuccess) {
        onUpdateSuccess();
      }
    } catch (error) {
      console.error("Error updating subscription:", error);
      setMessage({ 
        text: language === 'nl' ? 'Fout bij het bijwerken van abonnement' : 'Error updating subscription', 
        type: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  // Get tier icon component
  const getTierIcon = (tier: string) => {
    const config = TIER_CONFIG[tier.toLowerCase() as keyof typeof TIER_CONFIG] || TIER_CONFIG[SUBSCRIPTION_TIERS.NONE];
    const Icon = config.icon;
    return <Icon fontSize="small" />;
  };

  // Get tier color
  const getTierColor = (tier: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    const config = TIER_CONFIG[tier.toLowerCase() as keyof typeof TIER_CONFIG] || TIER_CONFIG[SUBSCRIPTION_TIERS.NONE];
    return config.color as "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning";
  };

  return (
    <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
      <Typography variant="h6" gutterBottom>
        {language === 'nl' ? 'ABONNEMENT BEHEREN' : 'MANAGE SUBSCRIPTION'}
      </Typography>
      <Divider sx={{ mb: 3 }} />
      
      {/* Current Subscription Display */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle2" gutterBottom>
          {language === 'nl' ? 'Huidige Status' : 'Current Status'}
        </Typography>
        <Paper 
          elevation={0} 
          sx={{ 
            p: 2, 
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: TIER_CONFIG[subscriptionTier.toLowerCase() as keyof typeof TIER_CONFIG]?.bgColor || 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Chip
              icon={getTierIcon(subscriptionTier)}
              label={getTierNameInDutch(subscriptionTier)}
              color={getTierColor(subscriptionTier)}
              size="medium"
              sx={{ mr: 2, fontWeight: 'bold' }}
            />
            {expiryDate && new Date(expiryDate) > new Date() ? (
              <Chip 
                icon={<CheckCircleIcon />} 
                label={language === 'nl' ? 'Actief' : 'Active'} 
                color="success" 
                variant="outlined" 
                size="small" 
              />
            ) : (
              <Chip 
                label={language === 'nl' ? 'Inactief' : 'Inactive'} 
                color="default" 
                variant="outlined" 
                size="small" 
              />
            )}
          </Box>
          
          {expiryDate && (
            <Typography variant="body2" color="text.secondary">
              {language === 'nl' ? 'Verloopt' : 'Expires'}: {new Date(expiryDate).toLocaleDateString()}
            </Typography>
          )}
        </Paper>
      </Box>
      
      {/* Tier Selection */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          {language === 'nl' ? 'Abonnement Niveau' : 'Subscription Tier'}
        </Typography>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          {Object.values(SUBSCRIPTION_TIERS).map((tier) => {
            const isSelected = subscriptionTier.toLowerCase() === tier.toLowerCase();
            const config = TIER_CONFIG[tier as keyof typeof TIER_CONFIG];
            const Icon = config.icon;
            
            return (
              <Grid item xs={6} sm={4} key={tier}>
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 2, 
                    border: '1px solid', 
                    borderColor: isSelected ? `${config.color}.main` : 'divider', 
                    borderRadius: 1,
                    cursor: 'pointer',
                    bgcolor: isSelected ? config.bgColor : 'transparent',
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: config.bgColor,
                      transform: 'translateY(-2px)',
                      boxShadow: 1
                    },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center'
                  }}
                  onClick={() => setSubscriptionTier(tier)}
                >
                  <Icon 
                    color={isSelected ? config.color as any : 'action'} 
                    sx={{ fontSize: 36, mb: 1 }} 
                  />
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      fontWeight: isSelected ? 'bold' : 'regular',
                      color: isSelected ? `${config.color}.main` : 'text.primary'
                    }}
                  >
                    {getTierNameInDutch(tier)}
                  </Typography>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Box>
      
      {/* Duration Settings */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom display="flex" alignItems="center">
          {language === 'nl' ? 'Abonnementsduur' : 'Subscription Duration'}
          <Tooltip title={language === 'nl' ? 'Standaard is 9 maanden' : 'Default is 9 months'}>
            <IconButton size="small" sx={{ ml: 1 }}>
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Typography>
        <TextField
          label={language === 'nl' ? 'Maanden' : 'Months'}
          type="number"
          value={months}
          onChange={(e) => {
            const value = parseInt(e.target.value);
            if (value > 0) {
              setMonths(value);
              setExpiryDate(calculateExpiryDate(value));
            }
          }}
          InputProps={{ inputProps: { min: 1 } }}
          fullWidth
        />
      </Box>
      
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          {language === 'nl' ? 'Vervaldatum' : 'Expiry Date'}
        </Typography>
        <TextField
          label={language === 'nl' ? 'Vervaldatum' : 'Expiry Date'}
          type="date"
          value={expiryDate}
          onChange={(e) => setExpiryDate(e.target.value)}
          fullWidth
          InputLabelProps={{ shrink: true }}
        />
      </Box>
      
      {message.text && (
        <Alert severity={message.type as 'success' | 'info' | 'warning' | 'error'} sx={{ mb: 3 }}>
          {message.text}
        </Alert>
      )}
      
      <Button
        variant="contained"
        color="primary"
        startIcon={<SaveIcon />}
        onClick={handleSaveSubscription}
        disabled={loading}
        fullWidth
      >
        {loading 
          ? (language === 'nl' ? 'Bezig met opslaan...' : 'Saving...') 
          : (language === 'nl' ? 'Abonnement opslaan' : 'Save Subscription')}
      </Button>
    </Paper>
  );
};

export default UserSubscriptionManager; 